/**
 * FloatingFilmi — draggable "Ask Filmi" mascot.
 *
 * - Drag (mouse or touch) to reposition freely.
 * - Tap/click (< 6px movement) navigates to /concierge.
 * - Snaps to nearest horizontal edge on release so it never floats mid-screen.
 * - Saves position to localStorage — persists across page loads.
 * - Hides on /concierge and /auth/* routes.
 */
import { useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FilmiMascot from "./FilmiMascot";

const STORAGE_KEY = "filmi_float_pos";
const DRAG_THRESHOLD = 6;   // px — below this = tap, above = drag
const EDGE_GAP = 12;        // px gap kept from screen edge after snap

function clamp(v, lo, hi) { return Math.min(Math.max(v, lo), hi); }

function loadSaved() {
  try {
    const s = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (!s || s.left == null || s.bottom == null) return null;
    // Clamp to current viewport in case screen size changed
    if (typeof window !== "undefined") {
      s.left   = clamp(s.left,   EDGE_GAP, window.innerWidth  - 120);
      s.bottom = clamp(s.bottom, EDGE_GAP, window.innerHeight - 120);
    }
    return s;
  } catch { return null; }
}

export default function FloatingFilmi() {
  const { pathname } = useLocation();
  const navigate     = useNavigate();

  // pos = { left, bottom } in px, or null → default bottom-right via CSS `right`
  const [pos,      setPos]      = useState(loadSaved);
  const [dragging, setDragging] = useState(false);
  const elRef = useRef(null);
  const ds    = useRef(null); // live drag state

  const hide = pathname?.startsWith("/concierge") || pathname?.startsWith("/auth");

  /* ─── pointer down: capture & record start position ─── */
  const onPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.preventDefault();
    const el = elRef.current;
    if (!el) return;
    try { el.setPointerCapture(e.pointerId); } catch {}
    const rect = el.getBoundingClientRect();
    ds.current = {
      startX:     e.clientX,
      startY:     e.clientY,
      initLeft:   rect.left,
      initBottom: window.innerHeight - rect.bottom,
      moved:      false,
    };
    setDragging(true);
  };

  /* ─── pointer move: live reposition ─── */
  const onPointerMove = (e) => {
    if (!ds.current) return;
    const dx = e.clientX - ds.current.startX;
    const dy = e.clientY - ds.current.startY;
    if (!ds.current.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    ds.current.moved = true;
    const el = elRef.current;
    if (!el) return;
    setPos({
      left:   clamp(ds.current.initLeft   + dx, EDGE_GAP, window.innerWidth  - el.offsetWidth  - EDGE_GAP),
      bottom: clamp(ds.current.initBottom - dy, EDGE_GAP, window.innerHeight - el.offsetHeight - EDGE_GAP),
    });
  };

  /* ─── pointer up: tap → navigate, drag → snap & save ─── */
  const onPointerUp = () => {
    if (!ds.current) return;
    const { moved } = ds.current;
    ds.current = null;
    setDragging(false);

    if (!moved) { navigate("/concierge"); return; }

    // Snap to nearest horizontal edge
    const el = elRef.current;
    if (!el) return;
    setPos(prev => {
      const curLeft = prev?.left ?? (window.innerWidth - el.offsetWidth - EDGE_GAP);
      const midX    = curLeft + el.offsetWidth / 2;
      const snapped = {
        left:   midX > window.innerWidth / 2
                  ? window.innerWidth - el.offsetWidth - EDGE_GAP
                  : EDGE_GAP,
        bottom: prev?.bottom ?? 52,
      };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(snapped)); } catch {}
      return snapped;
    });
  };

  if (hide) return null;

  // No saved pos → default bottom-right via CSS `right` property.
  // Once user drags, switch to explicit `left` + `bottom`.
  const posStyle = pos
    ? { left: pos.left, bottom: pos.bottom }
    : { right: EDGE_GAP, bottom: 52 };

  return (
    <div
      ref={elRef}
      data-testid="floating-filmi"
      aria-label="Ask Filmi"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/concierge"); }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => { ds.current = null; setDragging(false); }}
      className="fixed z-[95] flex items-center gap-2 group select-none"
      style={{
        ...posStyle,
        cursor:           dragging ? "grabbing" : "grab",
        touchAction:      "none",
        userSelect:       "none",
        WebkitUserSelect: "none",
        transition:       dragging ? "none" : "box-shadow 0.2s",
      }}
    >
      {/* Label — hidden on mobile */}
      <span className="hidden sm:inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-black/70 border border-white/15 backdrop-blur-md shadow-md group-hover:bg-[#E50914] transition-colors pointer-events-none">
        Ask Filmi
      </span>
      {/* Mascot */}
      <span className="relative inline-flex items-center justify-center group-hover:scale-110 transition-transform pointer-events-none">
        <span className="absolute inset-0 -m-2 blur-xl bg-[#E50914]/30 rounded-full pointer-events-none" />
        <FilmiMascot size={68} />
      </span>
    </div>
  );
}
